import { useEffect, useState, useCallback } from 'react';
import {
  Box, Chip, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField, MenuItem, Select,
  FormControl, InputLabel, Grid, IconButton, Tooltip, SelectChangeEvent,
} from '@mui/material';
import { Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import * as locales from 'date-fns/locale';
import { format as fnsFormat } from 'date-fns';
import { adminStyles } from '../../../theme/adminStyles';
import axios from 'axios';
import i18next, { getBrowserLanguage } from '../../../lang';

interface SystemLog {
  id: number;
  userId: string | null;
  action: string;
  targetType: string;
  targetId: string | null;
  detail: string | null;
  ipAddress: string | null;
  createdAt: string;
  organizationName: string | null;
}

const ACTIONS = ['', 'CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT', 'STATUS_CHANGE', 'APPROVE', 'REJECT'];
const TARGET_TYPES = ['', 'USER', 'ORDER', 'PRODUCT', 'PRODUCT_SET', 'INVOICE', 'ANNOUNCEMENT', 'MESSAGE', 'AUTH', 'ADDRESS', 'PRODUCT_MAPPING'];

const actionChipColor = (action: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  if (action === 'CREATE') return 'success';
  if (action === 'DELETE') return 'error';
  if (action === 'UPDATE' || action === 'STATUS_CHANGE') return 'warning';
  if (action === 'LOGIN' || action === 'LOGOUT') return 'info';
  return 'default';
};

const SystemLogTab = () => {
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [userId, setUserId] = useState('');
  const [action, setAction] = useState('');
  const [targetType, setTargetType] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const fetchLogs = useCallback(async () => {
    try {
      const params: Record<string, string | number> = { page, rowsPerPage };
      if (userId) params.userId = userId;
      if (action) params.action = action;
      if (targetType) params.targetType = targetType;
      if (startDate) params.startDate = fnsFormat(startDate, 'yyyy-MM-dd') + ' 00:00:00';
      if (endDate) params.endDate = fnsFormat(endDate, 'yyyy-MM-dd') + ' 23:59:59';

      const res = await axios.get('/api/v1/admin/service-status/system-logs', { params });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch system logs', err);
    }
  }, [page, rowsPerPage, userId, action, targetType, startDate, endDate]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleSearch = () => {
    setPage(0);
    fetchLogs();
  };

  const tzAbbr = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
    .formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value || '';

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : dateStr + 'Z');
    return d.toLocaleString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const truncateDetail = (detail: string | null) => {
    if (!detail) return '-';
    return detail.length > 80 ? detail.substring(0, 80) + '...' : detail;
  };

  const datePickerSx = { '& .MuiInputBase-root': { height: 40, fontSize: '0.85rem' }, '& .MuiInputLabel-root': { fontSize: '0.85rem' } };

  return (
    <>
      <Paper sx={adminStyles.filterPaper}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth size="small"
              label={i18next.t('service_status.user_id_or_name')}
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{i18next.t('service_status.action')}</InputLabel>
              <Select
                value={action}
                label={i18next.t('service_status.action')}
                onChange={(e: SelectChangeEvent) => { setAction(e.target.value); setPage(0); }}
              >
                {ACTIONS.map(a => (
                  <MenuItem key={a} value={a}>
                    {a === '' ? i18next.t('service_status.all') : a}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{i18next.t('service_status.target_type')}</InputLabel>
              <Select
                value={targetType}
                label={i18next.t('service_status.target_type')}
                onChange={(e: SelectChangeEvent) => { setTargetType(e.target.value); setPage(0); }}
              >
                {TARGET_TYPES.map(t => (
                  <MenuItem key={t} value={t}>
                    {t === '' ? i18next.t('service_status.all') : t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locales[getBrowserLanguage()] as any}>
              <DatePicker
                label={i18next.t('service_status.start_date')}
                value={startDate}
                maxDate={endDate || undefined}
                onChange={(v) => setStartDate(v)}
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: datePickerSx }, actionBar: { actions: ['clear'] } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locales[getBrowserLanguage()] as any}>
              <DatePicker
                label={i18next.t('service_status.end_date')}
                value={endDate}
                minDate={startDate || undefined}
                onChange={(v) => setEndDate(v)}
                slotProps={{ textField: { size: 'small', fullWidth: true, sx: datePickerSx }, actionBar: { actions: ['clear'] } }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton color="primary" onClick={handleSearch}><SearchIcon /></IconButton>
              <IconButton onClick={() => { setUserId(''); setAction(''); setTargetType(''); setStartDate(null); setEndDate(null); setPage(0); }}>
                <RefreshIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={adminStyles.tablePaper}>
        <TableContainer sx={{ flex: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{i18next.t('service_status.datetime')} ({tzAbbr})</TableCell>
                <TableCell>{i18next.t('service_status.user_id')}</TableCell>
                <TableCell>{i18next.t('service_status.organization')}</TableCell>
                <TableCell>{i18next.t('service_status.action')}</TableCell>
                <TableCell>{i18next.t('service_status.target_type')}</TableCell>
                <TableCell>{i18next.t('service_status.target_id')}</TableCell>
                <TableCell>{i18next.t('service_status.detail')}</TableCell>
                <TableCell>IP</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map(log => (
                <TableRow key={log.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{formatDate(log.createdAt)}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{log.userId || '-'}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{log.organizationName || '-'}</TableCell>
                  <TableCell>
                    <Chip label={log.action} size="small" color={actionChipColor(log.action)} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{log.targetType}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{log.targetId || '-'}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={log.detail || ''}><span>{truncateDetail(log.detail)}</span></Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{log.ipAddress || '-'}</TableCell>
                </TableRow>
              ))}
              {logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {i18next.t('service_status.no_data')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>
    </>
  );
};

export default SystemLogTab;
